import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { toast } from 'react-hot-toast'
import * as BABYLON from '@babylonjs/core'
import { useLocalStorage } from '@/hooks/common'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { maxPrintsForTier } from '@/lib/featureKeys'
import { IMAGES } from '@/assets/images'
import { LogoMark } from '@/features/album/components/EgowallLogo'
import { AlbumPhoto } from '@/features/album/types'
import { ShareSheet } from '@/features/album/components/ShareSheet'
import { useAlbumUpload } from '@/features/album/hooks/useAlbumUpload'
import { useAddNoteTopic, useUpdateNote, useDeleteNote } from '@/hooks/api/dashboard'
import { StudioAssetPhoto, StudioPin, StudioSceneState, StudioTool, StudioTopTab, ScenePresetId } from '../types'
import { SCENE_PRESET_GEOMETRY, STUDIO_PIN_NOTE_NAME, STUDIO_PIN_NOTE_TYPE, WALL_NAME } from '../constants'
import { getClassicFamilyForId, pickLaunchClassicTemplate } from '../classicCatalog'
import { printHeight } from '../babylon/sceneMath'
import { useStudio3DData, UseStudio3DDataResult, isLegacyPinId } from '../hooks/useStudio3DData'
import { useStudioEngine } from '../hooks/useStudioEngine'
import { useStudioRoom } from '../hooks/useStudioRoom'
import { useStudioArts } from '../hooks/useStudioArts'
import { useStudioLegacyRoom } from '../hooks/useStudioLegacyRoom'
import { useStudioCameraRig } from '../hooks/useStudioCameraRig'
import { useStudioTools } from '../hooks/useStudioTools'
import { useOverlayProjection } from '../hooks/useOverlayProjection'
import { useStudioTour } from '../hooks/useStudioTour'
import { useCommandPalette, StudioCommand } from '../hooks/useCommandPalette'
import { useStudioKeyboardShortcuts } from '../hooks/useStudioKeyboardShortcuts'
import { useStudioSceneHistory } from '../hooks/useStudioSceneHistory'
import { buildSceneState } from '../utils/sceneState'
import {
  nearestArtIdForPin,
  pinsBoundToArt,
  pinsForArt,
  resolvePinWorldTuple,
  stickyPinWorldTuple,
  stickySlotIndex,
} from '../utils/studioPins'
import { StudioTopBar, STUDIO_TOPBAR_HEIGHT } from '../components/StudioTopBar'
import { SaveScreenshotSheet } from '../components/SaveScreenshotSheet'
import { captureStudioScreenshot } from '../utils/captureStudioScreenshot'
import { StudioLayersPanel } from '../components/StudioLayersPanel'
import { RoomInspector, PrintInspector } from '../components/StudioInspector'
import { StudioToolbar } from '../components/StudioToolbar'
import { StudioViewSwitcher } from '../components/StudioViewSwitcher'
import { StudioOrbitSlider } from '../components/StudioOrbitSlider'
import { StudioTourBar } from '../components/StudioTourBar'
import { StudioCommandPalette } from '../components/StudioCommandPalette'
import { StudioOverlays } from '../components/StudioOverlays'
import { StudioFirstRunOverlay } from '../components/StudioFirstRunOverlay'
import { StudioCanvasLightbox } from '../components/StudioCanvasLightbox'
import { PinComposeSheet } from '../components/PinComposeSheet'
import { StudioLoadingVeil, STUDIO_LOAD_BG } from '../components/StudioLoadingVeil'
import { GlassPanel, monoLabelSx, ResetGlyph } from '../components/shared'
import { layoutClassicArts } from '../utils/classicAutoLayout'

type PinEditor =
  | { mode: 'create'; draft: StudioPin }
  | { mode: 'edit'; pinId: string }

/** Pin note body — everything but `id` (the real noteID is the canonical id). */
const serializePin = (pin: Omit<StudioPin, 'id'>) =>
  JSON.stringify({
    position: pin.position,
    authorName: pin.authorName,
    authorInitial: pin.authorInitial,
    text: pin.text,
    tone: pin.tone,
    artId: pin.artId,
    updatedAt: pin.updatedAt,
  })

const Stage = styled(Box)({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  background: STUDIO_LOAD_BG,
})

const Canvas = styled('canvas')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  touchAction: 'none',
  display: 'block',
  '&.studio-tool-hang, &.studio-tool-comment': { cursor: 'crosshair' },
})

const Coach = styled(GlassPanel)({
  position: 'absolute',
  top: 58,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 38,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 8px 8px 15px',
  borderRadius: 999,
  fontSize: 12,
  color: 'var(--ds-night-ink-muted)',
})

const ShutterFlash = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 90,
  pointerEvents: 'none',
  background: 'rgba(255,255,255,0.78)',
  animation: 'studioShutter 160ms ease-out forwards',
  '@keyframes studioShutter': {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
})

const Kbd = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  border: '1px solid var(--ds-night-border)',
  borderRadius: 5,
  padding: '2px 6px',
  color: 'var(--ds-night-ink)',
  background: 'rgba(244,243,246,0.06)',
})

const CoachClose = styled('button')({
  all: 'unset',
  width: 22,
  height: 22,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  '&:hover': { background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' },
})

const PresentHud = styled(GlassPanel)({
  position: 'absolute',
  left: '50%',
  bottom: 20,
  transform: 'translateX(-50%)',
  zIndex: 60,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px 8px 16px',
  borderRadius: 999,
  ...monoLabelSx,
  fontSize: 8.5,
})

const PresentHudSep = styled('span')({ width: 1, height: 16, flexShrink: 0, background: 'var(--ds-night-border)' })

/**
 * Present's own "get back to where I started" — a guest driving the camera
 * (drag to orbit, scroll to zoom, per the hint text this sits next to) has
 * no other way back once they've wandered off the room's framed pose,
 * unlike the Studio editor's Pan/Zoom pills (hidden here, see the
 * `!cameraRig.isPresent` mount guard below) which each have their own
 * recenter. Calls `cameraRig.resetCamera()` directly rather than
 * `exitPresent()` + `enterPresent()` — that round-trip would flash back to
 * the Studio editor chrome for a frame, and re-triggering Present's own
 * mount effects for what's really just a camera nudge.
 */
const PresentResetBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 20,
  height: 20,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--ds-night-ink-subtle)',
  transition: 'all 140ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.1)', color: 'var(--ds-night-ink)' },
  '& svg': { display: 'block' },
})

// The real brand mark (same `LogoMark` StudioTopBar uses), dimmed — the top
// bar itself is hidden in Present so this is the only brand cue left, in a
// corner nothing else ever renders into. Click exits Present, mirroring
// "logo = home" everywhere else.
const PresentBrand = styled(LogoMark)({
  position: 'absolute',
  top: 16,
  left: 16,
  zIndex: 60,
  opacity: 0.65,
  cursor: 'pointer',
  transition: 'opacity 140ms ease',
  '&:hover': { opacity: 1 },
})

/**
 * `/galleries/:galleryId/studio` — the Figma-grade Babylon 3D studio, ported
 * from the working prototype and promoted from its old `studio-next` path
 * once it replaced the legacy 2D `gallery-studio` editor. Phase-1 scope
 * decisions: Muse is a heuristic, presence is a labelled visual demo.
 *
 * The Babylon session only mounts after `seedReady` so `useStudioRoom` /
 * `useStudioArts` `useState(initial)` locks onto the real saved scene — not
 * `ROOM_DEFAULTS`. Mounting earlier caused a bright-then-dark flash and
 * autosave races that rewrote Ambience/Haze on every refresh.
 */
export function Studio3DScreen() {
  const { galleryId = '' } = useParams<{ galleryId: string }>()
  const data = useStudio3DData(galleryId)
  const navigate = useNavigate()

  // Studio is the live owner editor — every edit autosaves with no
  // confirmation. A non-owner (Discover/Search/Profile link, a bookmark, a
  // guessed id) gets bounced to the real read-only guest render instead of
  // ever seeing the edit surface. `/g/:id` already has the right chrome for
  // a signed-in-but-not-owner visitor — see GuestGalleryScreen's role model —
  // so this reuses it rather than building a second read-only view.
  useEffect(() => {
    if (data.seedReady && !data.isOwner) {
      navigate(`/g/${galleryId}`, { replace: true })
    }
  }, [data.seedReady, data.isOwner, galleryId, navigate])

  if (data.isError) {
    return (
      <Stage sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center', color: 'var(--ds-night-ink)' }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Gallery not found</Box>
          <Box sx={{ fontSize: 13, color: 'var(--ds-night-ink-subtle)' }}>
            This route expects a real GALLERY topic id — open one from /galleries first.
          </Box>
        </Box>
      </Stage>
    )
  }

  // Keep showing the veil (never mount the editor session) until we know
  // for sure this visitor owns the gallery — the redirect above takes over
  // the instant seedReady flips true and isOwner reads false.
  if (!data.seedReady || !data.isOwner) {
    return <StudioLoadingVeil label='OPENING STUDIO…' />
  }

  return <Studio3DSession key={galleryId} galleryId={galleryId} data={data} />
}

function Studio3DSession({ galleryId, data }: { galleryId: string; data: UseStudio3DDataResult }) {
  const navigate = useNavigate()
  const [ewUser] = useLocalStorage('ewUser', null)
  const { tier, showUpgrade } = useFeatureGate()
  const printCap = maxPrintsForTier(tier)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { scene } = useStudioEngine(canvasRef, true)
  const room = useStudioRoom(scene, data.initialRoom)
  const { width: roomW, height: roomH, depth: roomD } = room.room
  // Sourced from the live `room` state (not `data.initialRoom`, a one-time
  // load snapshot) so it tracks scene-preset swaps — otherwise print/spotlight
  // placement in useStudioArts keeps using stale dimensions from whichever
  // preset was active on mount (see setScenePreset in useStudioRoom).
  // Memoized on the primitive values (not the `room.room` object, which is a
  // fresh reference every render) so useStudioArts' roomDims-keyed effects
  // only actually re-run when the dimensions themselves change.
  const roomDims = useMemo(() => ({ width: roomW, height: roomH, depth: roomD }), [roomW, roomH, roomD])
  const mode = room.room.mode || 'studio'
  const artsApi = useStudioArts(scene, data.initialArts, roomDims, mode)
  const legacy = useStudioLegacyRoom(
    scene,
    mode === 'classic' ? room.room.classicTemplateId : undefined,
    room.room.wallColor,
    room.room.floorColor,
    room.room.ceilColor,
    artsApi.arts,
    artsApi.selectedId,
  )
  const cameraRig = useStudioCameraRig(scene, canvasRef, mode === 'classic' ? legacy.framing : null, mode === 'classic', 1, data.initialCamera)
  const projection = useOverlayProjection(scene, containerRef)

  useEffect(() => {
    if (legacy.error) toast.error(`Couldn't load that room — ${legacy.error}`)
  }, [legacy.error])

  // A Classic variant's real slot count often exceeds however many prints
  // are actually hung (fresh galleries, or a switch to a bigger variant) —
  // pad the gap with selectable-but-empty placeholders so every slot can be
  // clicked and filled from the Assets panel (see padClassicSlots' own doc
  // comment). Re-checked on every slotCount change (variant swap), and a
  // no-op once `arts` already covers it.
  useEffect(() => {
    if (mode !== 'classic' || !legacy.slotCount) return
    // Clamp to the plan cap too — a Classic variant's authored slot count
    // can exceed a free/Gallery room's print quota (see printCap above).
    artsApi.padClassicSlots(Math.min(legacy.slotCount, printCap))
  }, [mode, legacy.slotCount, artsApi.padClassicSlots, printCap])

  // Pins are real Notes on the gallery topic (STUDIO_PIN_NOTE_TYPE) — each
  // add/edit/delete below is its own note write, independent of the
  // room/arts/camera scene-blob autosave. See useStudio3DData's
  // parsePinNotes for why (full-blob overwrite races).
  const addPinNote = useAddNoteTopic()
  const updatePinNote = useUpdateNote()
  const deletePinNote = useDeleteNote(false)
  const topicID = data.topic?.topicID

  const [pins, setPins] = useState<StudioPin[]>(data.initialPins)
  const [pinEditor, setPinEditor] = useState<PinEditor | null>(null)
  /** Temporarily expand a sticky in-room (◈ from print STORY) without opening compose. */
  const [focusPinId, setFocusPinId] = useState<string | null>(null)
  const [topTab, setTopTab] = useState<StudioTopTab>('design')
  const [coachDismissed, setCoachDismissed] = useState(false)
  const [firstRunDismissed, setFirstRunDismissed] = useState(false)

  useEffect(() => {
    if (!focusPinId) return
    const t = window.setTimeout(() => setFocusPinId(null), 4000)
    return () => window.clearTimeout(t)
  }, [focusPinId])

  const initialScene = useMemo(
    () => buildSceneState(data.initialRoom, data.initialArts, data.initialPins),
    // Session remounts on galleryId; seed is fixed for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const history = useStudioSceneHistory(initialScene)
  const applyingHistoryRef = useRef(false)
  const roomStateRef = useRef(room.room)
  roomStateRef.current = room.room
  const artsRef = useRef(artsApi.arts)
  artsRef.current = artsApi.arts
  const pinsRef = useRef(pins)
  pinsRef.current = pins

  // One-time, per-session upgrade of any pins still embedded in the legacy
  // scene blob (saved before pins became real notes) into real notes — see
  // `isLegacyPinId`. Runs once right after mount, well before a user could
  // realistically open the pin editor, so touching an "old" pin almost never
  // races this. Not a backend migration script — just the same note-create
  // call a brand-new pin uses, applied lazily to whatever this gallery still
  // has embedded.
  const pinMigrationRanRef = useRef(false)
  useEffect(() => {
    if (pinMigrationRanRef.current || !topicID) return
    pinMigrationRanRef.current = true
    const legacyPins = pinsRef.current.filter((p) => isLegacyPinId(p.id))
    legacyPins.forEach((pin) => {
      addPinNote
        .mutateAsync({
          topicID,
          noteName: STUDIO_PIN_NOTE_NAME,
          noteText: serializePin(pin),
          noteType: STUDIO_PIN_NOTE_TYPE,
          egoItems: [],
        })
        .then((res) => {
          if (!res?.noteID) return
          setPins((prev) => prev.map((p) => (p.id === pin.id ? { ...p, id: res.noteID } : p)))
        })
        .catch(() => {
          // Stays embedded in the legacy scene blob for now — retried next load.
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicID])

  const commitHistory = useCallback(() => {
    requestAnimationFrame(() => {
      if (applyingHistoryRef.current) return
      history.commit(buildSceneState(roomStateRef.current, artsRef.current, pinsRef.current))
    })
  }, [history])

  const applySceneState = useCallback(
    (state: StudioSceneState) => {
      applyingHistoryRef.current = true
      room.applyRoomState(state.room)
      artsApi.replaceAllArts(state.arts)
      setPins(state.pins || [])
      setPinEditor(null)
      // Clear after React applies — next commit/rAF should see the restored doc.
      requestAnimationFrame(() => {
        applyingHistoryRef.current = false
      })
    },
    [room, artsApi],
  )

  const onUndo = useCallback(() => {
    const prev = history.undo()
    if (prev) applySceneState(prev)
  }, [history, applySceneState])

  const onRedo = useCallback(() => {
    const next = history.redo()
    if (next) applySceneState(next)
  }, [history, applySceneState])

  useEffect(() => {
    room.registerActiveCamera(cameraRig.getActiveCameraInfo)
  }, [room, cameraRig.getActiveCameraInfo])

  // Editor grid: brighter in plan, dim in orbit, hidden in walk — matches the mockup's setView().
  useEffect(() => {
    room.setGridMode(cameraRig.view)
  }, [room, cameraRig.view])

  // Preset swaps change room bounds — snap back to orbit home so cutaway
  // doesn't leave you outside the new shell (reads as a black void).
  const scenePreset = room.room.scenePreset || 'corridor'
  const presetCameraInit = useRef(true)
  useEffect(() => {
    if (presetCameraInit.current) {
      presetCameraInit.current = false
      return
    }
    if ((room.room.mode || 'studio') === 'classic') return
    cameraRig.setView('orbit')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenePreset])

  // Autosave — debounced inside data.persist(); fires on every edit to arts/room.
  // Session mounts only after seedReady, so the first persist already carries the
  // real saved room (not ROOM_DEFAULTS). Undo/redo also mark dirty here (no new
  // history entry — applyingHistoryRef) so the note stays aligned with the canvas.
  // Pins are NOT part of this — each pin is its own note, written independently
  // by applyPinEditor/deletePinEditor below (see useStudio3DData's parsePinNotes).
  const persistRef = useRef(data.persist)
  persistRef.current = data.persist
  useEffect(() => {
    // Empty-slot placeholders (padClassicSlots) are a pure runtime affordance
    // for clicking-to-fill a Classic room's unfilled slots — never worth
    // persisting; they're re-derived from the room's real slot count on
    // every load instead (see the padClassicSlots effect above).
    // `cameraRig.cameraState` is already debounced to "camera settled ~600ms
    // ago" (see useStudioCameraRig) — this effect firing on every value adds
    // data.persist()'s own 900ms debounce on top, same as any other edit.
    persistRef.current({
      room: room.room,
      arts: artsApi.arts.filter((a) => !a.isPlaceholder),
      camera: cameraRig.cameraState,
    })
  }, [room.room, artsApi.arts, cameraRig.cameraState])

  const flyToArt = useCallback(
    (id: string, artHint?: { side: -1 | 0 | 1; w: number }) => {
      const art = artHint ?? artsApi.arts.find((a) => a.id === id)
      const center = artsApi.getCenterFor(id)
      if (art && center) cameraRig.flyTo({ side: art.side, center, width: art.w })
    },
    [artsApi, cameraRig],
  )

  /** Canvas / tools — select only (mockup `select(a)` without fly). */
  const onSelectArt = useCallback(
    (id: string | null) => {
      artsApi.select(id)
      if (id) setCoachDismissed(true)
    },
    [artsApi],
  )

  /** Layers panel — select + fly (mockup `select(a, true)`). */
  const onSelectArtFromLayers = useCallback(
    (id: string) => {
      onSelectArt(id)
      flyToArt(id)
    },
    [onSelectArt, flyToArt],
  )

  const onDoubleClickArt = useCallback(
    (id: string) => {
      onSelectArt(id)
      flyToArt(id)
    },
    [onSelectArt, flyToArt],
  )

  const sourcePhotos = data.sourcePhotos

  /** Album palette first; if the gallery lost/never got sourceAlbumId, keep Assets in sync with hung wall prints. */
  const assetPhotos = useMemo((): StudioAssetPhoto[] => {
    const placeholder = IMAGES.topicPreviewPlaceholder
    const seen = new Set<string>()
    const out: StudioAssetPhoto[] = []

    const push = (photo: StudioAssetPhoto) => {
      if (!photo.url || photo.url === placeholder) return
      const key = photo.egoId || photo.url
      if (seen.has(key)) return
      seen.add(key)
      out.push({ ...photo, thumbUrl: photo.thumbUrl || photo.url })
    }

    for (const p of sourcePhotos) {
      push({
        id: p.id,
        url: p.url || p.thumbUrl || '',
        thumbUrl: p.thumbUrl,
        egoId: p.egoId,
        albumId: p.albumId,
        albumTitle: p.albumTitle,
      })
    }
    for (const a of artsApi.arts) {
      if (a.isPlaceholder) continue
      push({ id: a.egoId || a.id, url: a.img, thumbUrl: a.img, egoId: a.egoId })
    }

    return out.length > 0 ? out : [{ id: 'placeholder', url: placeholder }]
  }, [sourcePhotos, artsApi.arts])

  const hasRealAssetPhotos = assetPhotos.length > 0 && assetPhotos[0].id !== 'placeholder'
  const assetsFromWallOnly = hasRealAssetPhotos && sourcePhotos.length === 0

  const { uploadFiles, uploading: uploadingPhotos, progress: uploadProgress } = useAlbumUpload(
    undefined,
    data.refreshSourcePhotos,
  )

  const onUploadPhotos = useCallback(
    async (albumId: string, files: File[]) => {
      // Snapshot the slot selected at upload start — swapping whatever
      // finishes uploading into it mirrors the existing "select a print,
      // tap a pool photo" swap flow, just skipping the extra tap.
      const targetSlotId = artsApi.selectedId
      const uploaded = await uploadFiles(files, { albumId })
      if (targetSlotId && uploaded.length > 0) {
        const first = uploaded[0]
        const img = first.photoAlbumURL || first.photoFullURL
        if (img) {
          artsApi.swapPhoto(targetSlotId, { img, egoId: first.egoID })
          commitHistory()
          toast.success(
            uploaded.length > 1
              ? 'First photo dropped into the selected slot — rest added to Assets'
              : 'Photo dropped into the selected slot',
          )
        }
      }
    },
    [uploadFiles, artsApi, commitHistory],
  )

  const onHangWall = useCallback(
    (side: -1 | 0 | 1, point: BABYLON.Vector3) => {
      const livePrints = artsApi.arts.filter((a) => !a.isPlaceholder).length
      if (livePrints >= printCap) {
        showUpgrade({
          featureKey: FEATURE_KEYS.STUDIO_PRINT_QUOTA,
          headline: `Room is full at ${printCap} prints — upgrade to hang more`,
          upgradeTo: tier === 'pro' ? 'Studio' : 'Gallery',
        })
        return
      }
      const pool = assetPhotos.filter((p) => p.id !== 'placeholder')
      const photo = pool.length > 0 ? pool[artsApi.arts.length % pool.length] : undefined
      const img = photo?.url || photo?.thumbUrl || IMAGES.topicPreviewPlaceholder
      const art = artsApi.hangAt(side, point, { img, egoId: photo?.egoId })
      commitHistory()
      toast.success(`Hung "${art.name}" — swap the photo from Assets`, { duration: 3000 })
    },
    [artsApi, assetPhotos, commitHistory, printCap, showUpgrade, tier],
  )

  const setToolRef = useRef<(tool: StudioTool) => void>(() => {})

  const makeDraftPin = useCallback(
    (artId: string | undefined, hitPosition?: [number, number, number]) => {
      const room = roomStateRef.current
      const arts = artsRef.current
      const existing = artId ? pinsBoundToArt(artId, pinsRef.current) : []
      const slot = existing.length
      const art = artId ? arts.find((a) => a.id === artId) : undefined
      const position =
        art != null
          ? stickyPinWorldTuple(art, room, slot)
          : hitPosition ?? ([0, 1.6, 0] as [number, number, number])
      const draft: StudioPin = {
        id: `pin-${Date.now()}`,
        position,
        authorName: ewUser?.profileName || 'You',
        authorInitial: (ewUser?.profileName || 'You')[0]?.toUpperCase() || 'Y',
        text: '',
        tone: slot % 3 === 0 ? 'cyan' : slot % 3 === 1 ? 'gold' : 'violet',
        artId,
        updatedAt: Date.now(),
      }
      return draft
    },
    [ewUser],
  )

  const onPinPoint = useCallback(
    (point: BABYLON.Vector3, normal: BABYLON.Vector3) => {
      const pos = point.add(normal.scale(0.05))
      const hit: [number, number, number] = [pos.x, pos.y, pos.z]
      const artId = nearestArtIdForPin(hit, artsRef.current, roomStateRef.current)
      const draft = makeDraftPin(artId, hit)
      setPinEditor({ mode: 'create', draft })
      if (artId) artsApi.select(artId)
      setToolRef.current('select')
    },
    [makeDraftPin, artsApi],
  )

  const openPinEditor = useCallback(
    (pin: StudioPin) => {
      setFocusPinId(null)
      // Draft pin from an in-progress create — keep compose open, don't switch to edit.
      setPinEditor((cur) => {
        if (cur?.mode === 'create' && cur.draft.id === pin.id) return cur
        return { mode: 'edit', pinId: pin.id }
      })
      if (pin.artId) artsApi.select(pin.artId)
      setToolRef.current('select')
    },
    [artsApi],
  )

  const cancelPinEditor = useCallback(() => setPinEditor(null), [])

  const applyPinEditor = useCallback(
    (text: string) => {
      if (!pinEditor || !topicID) return
      if (pinEditor.mode === 'create') {
        const draft = pinEditor.draft
        let position = draft.position
        if (draft.artId) {
          const art = artsRef.current.find((a) => a.id === draft.artId)
          if (art) {
            const slot = pinsBoundToArt(draft.artId, pinsRef.current).length
            position = stickyPinWorldTuple(art, roomStateRef.current, slot)
          }
        }
        const pin: StudioPin = { ...draft, text, position, updatedAt: Date.now() }
        // Optimistic — the local temp id gets swapped for the real noteID
        // once the note-create call resolves, same pattern as the scene note.
        setPins((prev) => [...prev, pin])
        if (pin.artId) artsApi.update(pin.artId, { pinned: true })
        setPinEditor(null)
        commitHistory()
        toast.success('Note pinned — visible to everyone with the key', { duration: 3000 })
        addPinNote
          .mutateAsync({
            topicID,
            noteName: STUDIO_PIN_NOTE_NAME,
            noteText: serializePin(pin),
            noteType: STUDIO_PIN_NOTE_TYPE,
            egoItems: [],
          })
          .then((res) => {
            if (!res?.noteID) return
            setPins((prev) => prev.map((p) => (p.id === pin.id ? { ...p, id: res.noteID } : p)))
          })
          .catch(() => toast.error("Couldn't save that note — try again"))
        return
      }
      const pinId = pinEditor.pinId
      const current = pinsRef.current.find((p) => p.id === pinId)
      if (!current) {
        setPinEditor(null)
        return
      }
      const updated: StudioPin = { ...current, text, updatedAt: Date.now() }
      setPins((prev) => prev.map((p) => (p.id === pinId ? updated : p)))
      setPinEditor(null)
      commitHistory()
      if (isLegacyPinId(pinId)) {
        // Upgrade off the legacy scene-blob field the moment it's touched.
        addPinNote
          .mutateAsync({
            topicID,
            noteName: STUDIO_PIN_NOTE_NAME,
            noteText: serializePin(updated),
            noteType: STUDIO_PIN_NOTE_TYPE,
            egoItems: [],
          })
          .then((res) => {
            if (!res?.noteID) return
            setPins((prev) => prev.map((p) => (p.id === pinId ? { ...p, id: res.noteID } : p)))
          })
          .catch(() => toast.error("Couldn't save that note — try again"))
      } else {
        updatePinNote
          .mutateAsync({ noteID: pinId, topicID, noteText: serializePin(updated) })
          .catch(() => toast.error("Couldn't save that note — try again"))
      }
    },
    [pinEditor, topicID, artsApi, commitHistory, addPinNote, updatePinNote],
  )

  const deletePinEditor = useCallback(() => {
    if (!pinEditor || pinEditor.mode !== 'edit') return
    const pinId = pinEditor.pinId
    const removed = pinsRef.current.find((p) => p.id === pinId)
    setPins((prev) => prev.filter((p) => p.id !== pinId))
    setPinEditor(null)
    if (removed?.artId) {
      const remaining = pinsRef.current.filter((p) => p.id !== pinId && p.artId === removed.artId)
      if (remaining.length === 0) artsApi.update(removed.artId, { pinned: false })
    }
    commitHistory()
    toast('Pinned note removed', { icon: '◈' })
    // A legacy (never-migrated) pin has no backend note to delete — it simply
    // never gets written back into the scene blob going forward.
    if (topicID && !isLegacyPinId(pinId)) {
      deletePinNote.mutateAsync({ noteID: pinId, topicID }).catch(() => {
        toast.error("Couldn't delete that note — try again")
      })
    }
  }, [pinEditor, artsApi, commitHistory, topicID, deletePinNote])

  const tools = useStudioTools(scene, canvasRef, {
    onSelectArt,
    onDoubleClickArt,
    onHangWall,
    onPinPoint,
  })
  setToolRef.current = tools.setTool

  const onTourStep = useCallback(
    (art: { id: string; side: -1 | 0 | 1; w: number }) => {
      onSelectArt(art.id)
      flyToArt(art.id, art)
    },
    [onSelectArt, flyToArt],
  )
  const tour = useStudioTour(artsApi.arts, room.room.dwellSeconds, onTourStep)

  const onFirstRunConfirm = useCallback(
    (albumId: string, photos: AlbumPhoto[]) => {
      if (photos.length > 0) {
        // Classic is the launch default (task #17) — re-sizes to a template
        // that actually fits this album's photo count instead of the
        // 2-placeholder-slot template the stub seed started with.
        const templateId = pickLaunchClassicTemplate(photos.length)
        const arts = layoutClassicArts(photos.map((p) => ({ id: p.id, url: p.url, thumbUrl: p.thumbUrl, egoId: p.egoId })), templateId)
        room.setClassicTemplate(templateId)
        artsApi.replaceAllArts(arts)
        // Stamps sourceAlbumId into topicXData after the fact, since the stub
        // create had none — lets the Assets panel offer more of this album's
        // photos later, same as a wizard-created gallery gets at create time.
        data.setSourceAlbum(albumId)
        toast.success(`Gallery set up — ${arts.length} print${arts.length === 1 ? '' : 's'} hung`, { duration: 3000 })
      }
      commitHistory()
      setFirstRunDismissed(true)
    },
    [room, artsApi, data, commitHistory],
  )

  const onFirstRunSkip = useCallback(() => setFirstRunDismissed(true), [])

  const onMuse = useCallback(() => {
    artsApi.museReshuffle()
    commitHistory()
    toast.success('MUSE — rehung by pace & color, eye line normalized (heuristic, not AI)', { duration: 4200 })
  }, [artsApi, commitHistory])

  const onSetScenePreset = useCallback(
    (preset: ScenePresetId) => {
      const geo = SCENE_PRESET_GEOMETRY[preset]
      room.setScenePreset(preset)
      // Clamp freeform prints into the new footprint before history commit
      // (inspector calls onHistoryCommit right after this setter).
      artsApi.reflowToRoom({ width: geo.width, height: geo.height, depth: geo.depth })
    },
    [room, artsApi],
  )

  const onSetClassicTemplate = useCallback(
    (templateId: number) => {
      room.setClassicTemplate(templateId)
    },
    [room],
  )

  const [shareOpen, setShareOpen] = useState(false)
  const onShare = useCallback(() => setShareOpen(true), [])

  const [screenshotBusy, setScreenshotBusy] = useState(false)
  const [shutterFlash, setShutterFlash] = useState(false)
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null)
  const [screenshotOpen, setScreenshotOpen] = useState(false)

  const onScreenshot = useCallback(async () => {
    if (!scene || screenshotBusy) return
    setScreenshotBusy(true)
    setShutterFlash(true)
    window.setTimeout(() => setShutterFlash(false), 160)
    try {
      const file = await captureStudioScreenshot(scene)
      setScreenshotFile(file)
      setScreenshotOpen(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not capture screenshot')
    } finally {
      setScreenshotBusy(false)
    }
  }, [scene, screenshotBusy])

  const enterPresent = useCallback(() => cameraRig.enterPresent(), [cameraRig])
  const exitPresent = useCallback(() => cameraRig.exitPresent(), [cameraRig])

  // Dev-only Babylon Inspector toggle — `@babylonjs/inspector` is dynamically
  // imported so it never lands in the production bundle for everyone else.
  // StudioTopBar only renders the chip when this handler is actually passed.
  const inspectorHostRef = useRef<HTMLDivElement | null>(null)
  const removeInspectorHost = () => {
    inspectorHostRef.current?.remove()
    inspectorHostRef.current = null
  }
  const onToggleInspector = useCallback(async () => {
    if (!scene) return
    if (scene.debugLayer.isVisible()) {
      scene.debugLayer.hide()
      removeInspectorHost()
      return
    }
    await import('@babylonjs/inspector')
    // With no `globalRoot`, Babylon's embedMode injects its DOM node
    // straight into the canvas's parent — our React-managed `Stage`. React
    // has no idea that node exists, so the next time it reconciles that
    // subtree (toggling this off, or navigating away from Studio) it tries
    // to remove children it thinks it owns and throws "removeChild: node
    // is not a child of this node" on whatever Babylon quietly inserted.
    // A detached host appended straight to <body> — outside React's tree
    // entirely — sidesteps that: we own its lifecycle end to end, React
    // never touches it.
    const host = document.createElement('div')
    host.style.position = 'fixed'
    host.style.inset = '0'
    host.style.zIndex = '999999'
    // The inspector's own panel is a small docked strip inside this
    // full-viewport host — without this, the empty space around it would
    // still swallow every click/scroll meant for the canvas underneath.
    host.style.pointerEvents = 'none'
    document.body.appendChild(host)
    inspectorHostRef.current = host
    await scene.debugLayer.show({ embedMode: true, globalRoot: host })
    // Babylon docks the embedded inspector flush to `top: 0` on its own
    // host (`#embed-host`), same corner StudioTopBar occupies — push it
    // down below our bar so the two don't overlap.
    const embedHost = host.querySelector<HTMLElement>('#embed-host')
    if (embedHost) {
      embedHost.style.top = `${STUDIO_TOPBAR_HEIGHT}px`
      embedHost.style.pointerEvents = 'auto'
    }
  }, [scene])

  // Belt-and-suspenders — if the screen unmounts (nav away) while the
  // inspector is still open, clean up the detached host ourselves; nothing
  // else will, since React was never aware it existed.
  useEffect(() => removeInspectorHost, [])

  /** Hung prints only — lightbox filmstrip skips Classic slot placeholders. */
  const lightboxArts = useMemo(() => artsApi.arts.filter((a) => !a.isPlaceholder && a.img), [artsApi.arts])
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const onOpenLightbox = useCallback(() => {
    const selectedId = artsApi.selectedId
    if (!selectedId) return
    const idx = lightboxArts.findIndex((a) => a.id === selectedId)
    if (idx < 0) return
    setLightboxIndex(idx)
  }, [artsApi.selectedId, lightboxArts])

  const lightboxPins = useMemo(() => {
    if (lightboxIndex == null) return []
    const art = lightboxArts[lightboxIndex]
    if (!art) return []
    return pinsForArt(art, pins, room.room)
  }, [lightboxIndex, lightboxArts, pins, room.room])

  const overlayPins = useMemo(() => {
    if (pinEditor?.mode === 'create') return [...pins, pinEditor.draft]
    return pins
  }, [pins, pinEditor])

  const editingPin = useMemo(() => {
    if (!pinEditor) return null
    if (pinEditor.mode === 'create') return pinEditor.draft
    return pins.find((p) => p.id === pinEditor.pinId) || null
  }, [pinEditor, pins])

  const pinComposePrintLabel = useMemo(() => {
    const artId = editingPin?.artId
    if (!artId) return null
    return artsApi.arts.find((a) => a.id === artId)?.name || null
  }, [editingPin, artsApi.arts])

  const lightboxRoomLabel = useMemo(() => {
    if (mode === 'classic') return getClassicFamilyForId(room.room.classicTemplateId ?? -1)?.label || 'Classic'
    return SCENE_PRESET_GEOMETRY[room.room.scenePreset || 'corridor']?.label || 'Corridor'
  }, [mode, room.room.classicTemplateId, room.room.scenePreset])

  const onToggleVisible = artsApi.toggleVisible
  // Derived straight from `arts` (the persisted `hidden` flag) instead of a
  // separately hand-synced Set — `StudioLayersPanel`'s eye icons always
  // match whatever actually got saved, including right after a reload.
  const hiddenIds = useMemo(() => new Set(artsApi.arts.filter((a) => a.hidden).map((a) => a.id)), [artsApi.arts])

  const commands: StudioCommand[] = useMemo(
    () => [
      { id: 'muse', icon: '✦', label: 'Rehang with Muse', run: onMuse },
      { id: 'walk', icon: '👁', label: 'Walk the room (guest view)', shortcut: '3', run: () => cameraRig.setView('walk') },
      { id: 'plan', icon: '▦', label: 'Plan view', shortcut: '2', run: () => cameraRig.setView('plan') },
      { id: 'orbit', icon: '◍', label: 'Orbit view', shortcut: '1', run: () => cameraRig.setView('orbit') },
      { id: 'hang', icon: '🖼', label: 'Hang a print', shortcut: 'H', run: () => tools.setTool('hang') },
      { id: 'pin', icon: '💬', label: 'Pin a note', shortcut: 'C', run: () => tools.setTool('comment') },
      { id: 'present', icon: '▶', label: 'Present to guests', run: enterPresent },
      { id: 'screenshot', icon: '📷', label: 'Screenshot → save to album', run: () => { void onScreenshot() } },
      { id: 'share', icon: '⚿', label: 'Share…', run: onShare },
      {
        id: 'wall-white',
        icon: '🎨',
        label: 'Wall — Gallery White',
        run: () => {
          room.setWallColor('#efece4')
          commitHistory()
        },
      },
      {
        id: 'wall-ink',
        icon: '🌒',
        label: 'Wall — Ink',
        run: () => {
          room.setWallColor('#1b1722')
          commitHistory()
        },
      },
      { id: 'undo', icon: '↩', label: 'Undo', shortcut: '⌘Z', run: onUndo },
      { id: 'redo', icon: '↪', label: 'Redo', shortcut: '⇧⌘Z', run: onRedo },
    ],
    [onMuse, cameraRig, tools, enterPresent, room, onShare, onScreenshot, commitHistory, onUndo, onRedo],
  )
  const palette = useCommandPalette(commands)

  useStudioKeyboardShortcuts({
    // Tour/Present are guest-facing preview modes (their whole point is
    // hiding editor chrome — see the panel-hiding for task #14) — leaving
    // edit shortcuts live let Backspace, F, 1/2/3, and ⌘Z/⇧⌘Z all still
    // fire underneath and fight the tour's own camera animation or quietly
    // mutate the scene while nothing on screen suggests editing is possible.
    enabled: !palette.isOpen && topTab !== 'tour' && !cameraRig.isPresent,
    onSetTool: (tool) => {
      // Mirrors `StudioToolbar`'s own `hangDisabled={mode === 'classic'}` —
      // Classic has no "hang a fresh print" surface (fixed slots, no bare
      // wall to click), so the H shortcut would otherwise arm a tool that
      // can never actually do anything there.
      if (tool === 'hang' && mode === 'classic') return
      tools.setTool(tool)
    },
    onSetView: cameraRig.setView,
    onFlyToSelected: () => artsApi.selectedId && flyToArt(artsApi.selectedId),
    onDeleteSelected: () => {
      // Classic slots are fixed room geometry, not real independent prints —
      // `PrintInspector` already hides its own Remove button for them
      // (`locked={mode === 'classic'}`); this global shortcut skipped that
      // same guard. Removing one from `arts` doesn't just leave the slot
      // empty either — Classic slots align positionally (array index ->
      // room slot index, see `useStudioLegacyRoom`), so it would shift
      // every later slot's photo back by one instead.
      if (!artsApi.selectedId || mode === 'classic') return
      artsApi.remove(artsApi.selectedId)
      commitHistory()
    },
    onEscape: () => {
      if (cameraRig.isPresent) exitPresent()
      else if (artsApi.selectedId) artsApi.select(null)
    },
    onUndo,
    onRedo,
  })

  const selectedArtForTag = useMemo(() => {
    if (!artsApi.selectedArt) return null
    return {
      id: artsApi.selectedArt.id,
      name: artsApi.selectedArt.name,
      w: artsApi.selectedArt.w,
      ratio: artsApi.selectedArt.ratio,
      wallLabel: WALL_NAME[artsApi.selectedArt.side],
    }
  }, [artsApi.selectedArt])

  const getSelectedTagPos = useCallback(() => {
    if (!artsApi.selectedArt) return null
    const center = artsApi.getCenterFor(artsApi.selectedArt.id)
    if (!center) return null
    const height = printHeight(artsApi.selectedArt)
    return center.add(new BABYLON.Vector3(0, height / 2 + 0.35, 0))
  }, [artsApi])

  const getPinPos = useCallback(
    (pin: StudioPin) => {
      const tuple = resolvePinWorldTuple(pin, artsRef.current, pinsRef.current, roomStateRef.current)
      // Include draft in stack index while composing a new sticky on a print.
      if (pinEditor?.mode === 'create' && pinEditor.draft.id === pin.id && pin.artId) {
        const withDraft = [...pinsRef.current, pinEditor.draft]
        const slot = stickySlotIndex(pin, withDraft)
        const art = artsRef.current.find((a) => a.id === pin.artId)
        if (art) {
          return new BABYLON.Vector3(...stickyPinWorldTuple(art, roomStateRef.current, slot))
        }
      }
      return new BABYLON.Vector3(...tuple)
    },
    [pinEditor],
  )
  const presencePos = useCallback(() => {
    const t = performance.now() / 1000
    return new BABYLON.Vector3(3.4 + Math.sin(t * 0.14) * 1.6, 1.8 + Math.sin(t * 0.6) * 0.14, Math.cos(t * 0.11) * 4.2)
  }, [])

  return (
    <Stage ref={containerRef}>
      <Canvas ref={canvasRef} id='studio3d-canvas' />

      {!cameraRig.isPresent && (
        <>
          <StudioTopBar
            ownerName={ewUser?.profileName || 'You'}
            fileName={data.galleryTitle}
            topicID={galleryId}
            tab={topTab}
            onTabChange={setTopTab}
            saving={data.saving}
            savedVersion={data.savedVersion}
            onShare={onShare}
            onPresent={enterPresent}
            onScreenshot={() => { void onScreenshot() }}
            screenshotBusy={screenshotBusy}
            onLogoClick={() => {
              data.flushPersist()
              navigate('/galleries')
            }}
            onToggleInspector={process.env.NODE_ENV === 'development' ? onToggleInspector : undefined}
          />

          {/* Design/Tour tab — task #14. Tour is a clean flythrough preview,
              not an edit surface: both side panels hide the instant you
              switch to it, same "automatic, no toggle" call already made
              for Present (owners liked not having to manage visibility
              themselves). The way back is the always-visible Design/Tour
              pill in the top bar above, one click, no exit flow needed —
              deliberately lighter than Present's ESC-to-exit since Tour
              stays inside the editor route the whole time. */}
          {topTab !== 'tour' && (
            <>
              <StudioLayersPanel
                arts={artsApi.arts}
                selectedId={artsApi.selectedId}
                hiddenIds={hiddenIds}
                onSelectArt={onSelectArtFromLayers}
                onDoubleClickArt={onDoubleClickArt}
                onToggleVisible={onToggleVisible}
                onSelectRoom={() => onSelectArt(null)}
                onHangClick={() => tools.setTool('hang')}
                pins={pins}
                onSelectPin={openPinEditor}
                assetPhotos={assetPhotos}
                assetsLoading={data.sourcePhotosLoading}
                hasRealPhotos={hasRealAssetPhotos}
                assetsFromWallOnly={assetsFromWallOnly}
                linkedAlbums={data.linkedAlbums}
                onAddSourceAlbum={data.addSourceAlbum}
                onRemoveSourceAlbum={data.removeSourceAlbum}
                onPickAsset={(photo) => {
                  if (artsApi.selectedId) {
                    artsApi.swapPhoto(artsApi.selectedId, { img: photo.url || photo.thumbUrl || '', egoId: photo.egoId })
                    commitHistory()
                    toast.success('Photo swapped — frame and light kept')
                  } else {
                    toast('Select a print first — or press H to hang it fresh', { icon: '➤' })
                  }
                }}
                onUploadPhotos={onUploadPhotos}
                uploadingPhotos={uploadingPhotos}
                uploadProgress={uploadProgress}
              />

              {artsApi.selectedArt ? (
                <PrintInspector
                  art={artsApi.selectedArt}
                  onRename={(name) => artsApi.update(artsApi.selectedArt!.id, { name })}
                  onUpdate={(patch) => artsApi.update(artsApi.selectedArt!.id, patch)}
                  onRemove={() => {
                    toast(`"${artsApi.selectedArt!.name}" taken down`, { icon: '↩' })
                    artsApi.remove(artsApi.selectedArt!.id)
                  }}
                  onOpenLightbox={onOpenLightbox}
                  locked={mode === 'classic'}
                  onHistoryCommit={commitHistory}
                  storyPins={pinsBoundToArt(artsApi.selectedArt.id, pins)}
                  showPins={room.room.showPins}
                  onRevealPins={() => {
                    const bound = pinsBoundToArt(artsApi.selectedArt!.id, pinsRef.current)
                    if (bound.length === 0) return
                    if (!room.room.showPins) {
                      room.setShowPins(true)
                      commitHistory()
                    }
                    setFocusPinId(bound[0].id)
                  }}
                />
              ) : (
                <RoomInspector
                  title={data.galleryTitle}
                  printCount={artsApi.arts.filter((a) => !a.isPlaceholder).length}
                  room={room.room}
                  onRename={data.renameGallery}
                  onSetWallColor={room.setWallColor}
                  onSetFloorColor={room.setFloorColor}
                  onSetCeilColor={room.setCeilColor}
                  onSetAmbience={room.setAmbience}
                  onSetFog={room.setFog}
                  onSetDwellSeconds={room.setDwellSeconds}
                  onSetShowPins={room.setShowPins}
                  onSetShowPresenceDemo={room.setShowPresenceDemo}
                  onSetUseHdri={room.setUseHdri}
                  onSetScenePreset={onSetScenePreset}
                  onSetClassicTemplate={onSetClassicTemplate}
                  classicLoading={legacy.loading}
                  classicError={legacy.error}
                  onHistoryCommit={commitHistory}
                />
              )}
            </>
          )}

          <StudioToolbar
            tool={tools.tool}
            onToolChange={tools.setTool}
            onMuse={onMuse}
            hangDisabled={mode === 'classic'}
            onUndo={onUndo}
            onRedo={onRedo}
            canUndo={history.canUndo}
            canRedo={history.canRedo}
          />
          <StudioViewSwitcher
            view={cameraRig.view}
            zoomPercent={cameraRig.zoomPercent}
            onViewChange={cameraRig.setView}
            onZoomIn={cameraRig.zoomIn}
            onZoomOut={cameraRig.zoomOut}
            onResetZoom={cameraRig.resetZoom}
            canWalk={cameraRig.canWalk}
          />

          {/* Orbit-only — Plan is a fixed top-down layout and Walk has its
              own WASD navigation. Shown for Classic rooms too: alpha is
              locked there, but useStudioCameraRig pans `target.x` instead
              (see orbitAlphaPercent's doc comment) — the same axis the
              legacy pattern editor's own camera-position slider panned. */}
          {cameraRig.view === 'orbit' && (
            <StudioOrbitSlider value={cameraRig.orbitAlphaPercent} onChange={cameraRig.setOrbitAlphaPercent} />
          )}

          {topTab === 'tour' && (
            <StudioTourBar
              arts={artsApi.arts}
              activeIndex={tour.activeIndex}
              isPlaying={tour.isPlaying}
              onPlayToggle={tour.play}
              onJumpTo={tour.jumpTo}
              variant='tour'
            />
          )}

          {!coachDismissed && (
            <Coach>
              Click any print to edit it live — or press <Kbd>⌘K</Kbd> for anything
              <CoachClose onClick={() => setCoachDismissed(true)}>✕</CoachClose>
            </Coach>
          )}
        </>
      )}

      {lightboxIndex == null && (
        <StudioOverlays
          projection={projection}
          selectedArt={selectedArtForTag}
          getSelectedTagPos={getSelectedTagPos}
          pins={overlayPins}
          getPinPos={getPinPos}
          showPins={room.room.showPins || !!pinEditor}
          showPresenceDemo={!!room.room.showPresenceDemo}
          presencePos={presencePos}
          reveal={cameraRig.isPresent ? 'click' : 'hover'}
          onPinClick={cameraRig.isPresent ? undefined : openPinEditor}
          activePinId={cameraRig.isPresent ? focusPinId : editingPin?.id ?? focusPinId}
        />
      )}

      {pinEditor && editingPin && lightboxIndex == null && !cameraRig.isPresent && (
        <PinComposeSheet
          mode={pinEditor.mode}
          initialText={editingPin.text}
          printLabel={pinComposePrintLabel}
          onApply={applyPinEditor}
          onCancel={cancelPinEditor}
          onDelete={pinEditor.mode === 'edit' ? deletePinEditor : undefined}
        />
      )}

      {lightboxIndex != null && lightboxArts.length > 0 && (
        <StudioCanvasLightbox
          arts={lightboxArts}
          index={Math.min(lightboxIndex, lightboxArts.length - 1)}
          roomLabel={lightboxRoomLabel}
          galleryTitle={data.galleryTitle}
          pins={lightboxPins}
          onIndexChange={(i) => {
            setLightboxIndex(i)
            const next = lightboxArts[i]
            if (next) artsApi.select(next.id)
          }}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {cameraRig.isPresent && (
        <>
          <PresentBrand size={20} aria-label='Exit Present, back to Studio' onClick={exitPresent} />
          <PresentHud>
            <span>
              {cameraRig.canWalk ? (
                <>
                  <Kbd>DRAG</Kbd> LOOK · <Kbd>W A S D</Kbd> WALK
                </>
              ) : (
                <>
                  <Kbd>DRAG</Kbd> ORBIT · <Kbd>SCROLL</Kbd> ZOOM
                </>
              )}
            </span>
            <PresentHudSep />
            <PresentResetBtn type='button' aria-label='Reset camera to starting view' title='Reset camera' onClick={() => cameraRig.resetCamera()}>
              <ResetGlyph size={13} />
            </PresentResetBtn>
            <Box component='span' sx={{ cursor: 'pointer' }} onClick={exitPresent}>
              <Kbd>ESC — BACK TO STUDIO</Kbd>
            </Box>
          </PresentHud>
        </>
      )}

      {data.isNewGallery && !firstRunDismissed && (
        <StudioFirstRunOverlay onConfirm={onFirstRunConfirm} onSkip={onFirstRunSkip} />
      )}

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={data.shareLink}
        title={data.galleryTitle}
        isPrivate={data.isPrivate}
        onTogglePrivacy={data.togglePrivacy}
      />

      {shutterFlash && <ShutterFlash aria-hidden />}

      <SaveScreenshotSheet
        open={screenshotOpen}
        file={screenshotFile}
        galleryTitle={data.galleryTitle}
        onClose={() => {
          setScreenshotOpen(false)
          setScreenshotFile(null)
        }}
      />

      <StudioCommandPalette
        isOpen={palette.isOpen}
        query={palette.query}
        commands={palette.filtered}
        onQueryChange={palette.setQuery}
        onRun={palette.run}
        onClose={palette.close}
      />
    </Stage>
  )
}
